import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { StateService } from './state.service';
import { UserSettingsService } from '../api/user-service';
import { KeycloakService } from 'keycloak-angular';

interface UserSettingsState {
  isInitialized: boolean;
  maxProceduresPerPage: number;
  favoriteProcedures: number[];
  hiddenProcedures: number[];
  newsletterOptIn: boolean | null;
}

const initialState: UserSettingsState = {
  isInitialized: false,
  maxProceduresPerPage: 10,
  favoriteProcedures: [],
  hiddenProcedures: [],
  newsletterOptIn: false,
};

@Injectable({
  providedIn: 'root',
})
export class UserSettingsStateService extends StateService<UserSettingsState> {
  isInitialized$: Observable<boolean> = this.select(state => state.isInitialized).pipe(filter(Boolean));
  maxProceduresPerPage$: Observable<number> = this.select(state => state.maxProceduresPerPage);
  favoriteProcedures$: Observable<number[]> = this.select(state => state.favoriteProcedures);
  hiddenProcedures$: Observable<number[]> = this.select(state => state.hiddenProcedures);
  newsletterOptIn$: Observable<boolean | null> = this.select(state => state.newsletterOptIn);

  constructor(
    private userSettingsService: UserSettingsService,
    private keycloak: KeycloakService,
  ) {
    super(initialState);

    this.loadUserSettings();
  }

  private loadUserSettings(): void {
    from(this.keycloak.isLoggedIn()).pipe(
      filter(isLoggedIn => isLoggedIn),
      switchMap(() => this.userSettingsService.getUserSettings()),
    ).subscribe(settings => {
      this.setState({
        isInitialized: true,
        maxProceduresPerPage: settings.maxProceduresPerPage,
        favoriteProcedures: settings.favoriteProcedures,
        hiddenProcedures: settings.hiddenProcedures,
        newsletterOptIn: settings.newsletterOptIn,
      });
    });
  }

  updatePageSize(size: number): Observable<void> {
    this.setState({ maxProceduresPerPage: size });
    return this.saveSettings();
  }

  updateFavoriteProcedures(procedureId: number): Observable<void> {
    this.setState({ favoriteProcedures: this.toggleProcedureId(this.state.favoriteProcedures, procedureId) });
    return this.saveSettings();
  }

  updateHiddenProcedures(procedureId: number): Observable<void> {
    this.setState({ hiddenProcedures: this.toggleProcedureId(this.state.hiddenProcedures, procedureId) });
    return this.saveSettings();
  }

  updateNewsletterOptIn(optIn: boolean): Observable<void> {
    this.setState({ newsletterOptIn: optIn });
    return this.saveSettings();
  }

  private toggleProcedureId(list: number[], id: number): number[] {
    return list.includes(id) ? list.filter(item => item !== id) : [...list, id];
  }

  private saveSettings(): Observable<void> {
    const { maxProceduresPerPage, favoriteProcedures, hiddenProcedures, newsletterOptIn } = this.state;
    return from(this.keycloak.loadUserProfile()).pipe(
      switchMap(profile => {
        return this.userSettingsService.createOrUpdateUserSettings({
          userId: profile.id,
          maxProceduresPerPage,
          favoriteProcedures,
          hiddenProcedures,
          newsletterOptIn,
        })
      }),
    )
  }

  removeProcedureFromFavoriteAndHiddenLists(procedureId: number): Observable<void> {
    return this.userSettingsService.removeProcedureFromFavoriteAndHiddenLists(procedureId).pipe(
      tap(() => {
        this.setState({
          favoriteProcedures: this.state.favoriteProcedures.filter(id => id !== procedureId),
          hiddenProcedures: this.state.hiddenProcedures.filter(id => id !== procedureId),
        });
      }),
    );
  }
}
